import { useState, type FormEvent } from 'react'
import { ApiError } from '../api/client'
import { useAuth } from '../auth/AuthProvider'

/**
 * Форма входа (А-101).
 *
 * Проработаны состояния экрана: покой, отправка, ошибка (ДС-10).
 * Формулировка ошибки повторяет ответ серверной части и не различает
 * несуществующий адрес и неверный пароль.
 */
export default function LoginPage() {
  const { login , expired } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setPending(true)
    try {
      await login(email.trim(), password)
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          err.retryAfter
            ? `${err.message}. Повторите через ${Math.ceil(err.retryAfter / 60)} мин.`
            : err.message,
        )
      } else {
        setError('Сервер недоступен. Проверьте соединение и повторите.')
      }
      setPending(false)
    }
  }

  return (
    <main className="flex min-h-svh items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <header className="mb-8">
          <div className="mb-5 flex size-11 items-center justify-center rounded-xl bg-brand-600 text-lg font-semibold text-white">
            К
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink-900 dark:text-ink-50">
            Каталог и склад
          </h1>
          <p className="mt-1.5 text-sm text-ink-500">
            Вход в административное приложение
          </p>
        </header>

        <form onSubmit={onSubmit} noValidate>
          <div className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="mb-1.5 block text-sm font-medium text-ink-700 dark:text-ink-300"
              >
                Адрес почты
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={pending}
                aria-invalid={error !== null}
                className="w-full rounded-md border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 shadow-xs transition-colors placeholder:text-ink-400 focus:border-brand-500 disabled:opacity-60 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-50"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-1.5 block text-sm font-medium text-ink-700 dark:text-ink-300"
              >
                Пароль
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={pending}
                aria-invalid={error !== null}
                className="w-full rounded-md border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 shadow-xs transition-colors placeholder:text-ink-400 focus:border-brand-500 disabled:opacity-60 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-50"
              />
            </div>
          </div>

          {expired && !error && (

            <p className="mt-4 rounded-md border border-warning-500/30 bg-warning-100 px-3 py-2 text-sm text-warning-700 dark:bg-warning-700/25 dark:text-warning-100">

              Сессия закончилась — войдите снова. Несохранённые правки могли не примениться.

            </p>

          )}

          {error && (
            <p
              role="alert"
              className="mt-4 rounded-md border border-danger-500/30 bg-danger-100 dark:bg-danger-700/25 px-3 py-2 text-sm text-danger-700 dark:text-danger-100"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={pending || email === '' || password === ''}
            className="mt-6 w-full rounded-md bg-brand-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {pending ? 'Проверка…' : 'Войти'}
          </button>
        </form>

        {import.meta.env.DEV && (
          <p className="mt-8 rounded-md border border-ink-200 bg-ink-50 px-3 py-2.5 text-xs leading-relaxed text-ink-500 dark:border-ink-800 dark:bg-ink-900">
            Среда разработки. Учётная запись платформы:{' '}
            <code className="font-mono text-ink-700 dark:text-ink-300">test@test.kz</code> /{' '}
            <code className="font-mono text-ink-700 dark:text-ink-300">test</code>. Учётные
            записи компании перечислены в выводе <code className="font-mono">make seed</code>.
          </p>
        )}
      </div>
    </main>
  )
}
